import React from "react";
import { Link } from "react-router-dom";
import { Heart, Shield, Sparkles, Users, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import { useReveal } from "../lib/motion";

const PRINCIPLES = [
    { icon: Shield,   title: "Privacy first",    text: "Your health data is yours. End-to-end encrypted. Never sold. Always exportable." },
    { icon: Heart,    title: "Calm by design",   text: "No anxiety-inducing notifications. No shame. Just gentle, well-timed support." },
    { icon: Sparkles, title: "Smart, not invasive", text: "AI that learns your patterns — without ever feeling creepy or judgmental." },
    { icon: Users,    title: "Community matters", text: "Every woman deserves access to evidence-based health information and peer support." },
];

const STATS = [];

export default function About() {
    useReveal();

    return (
        <div data-testid="about-page">
            <PageHero
                testId="about-hero"
                overline="Our story"
                title={
                    <>
                        Built by women.<br />
                        <span className="italic font-light">For women.</span>
                    </>
                }
                size="lg"
            />

            {/* STORY */}
            <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
                    <div className="md:col-span-7 reveal">
                        <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-femcare-pink/20 group">
                            <img
                                src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1200&q=80"
                                alt="Women from the Femcare community sharing support together"
                                className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-femcare-purple/30 via-transparent to-transparent" />
                            <div className="absolute bottom-6 left-6 glass rounded-full px-4 py-2 text-xs uppercase tracking-[0.25em] text-femcare-purple">
                                Est. 2023 · Bengaluru
                            </div>
                        </div>
                    </div>
                    <div className="md:col-span-5 prose-femcare pt-2 reveal reveal-delay-1">
                        <p className="text-lg leading-relaxed">
                            Femcare began in 2023 with a simple frustration: most cycle tracking apps were
                            either clinical and cold, or fluffy and inaccurate. Nothing felt designed for
                            the way modern women actually live.
                        </p>
                        <p className="text-lg leading-relaxed">
                            So our team of OB-GYNs, women engineers, and product designers set out to build
                            something different — calm, intelligent, private. An app that respects your data
                            and your time.
                        </p>
                        <p className="text-lg leading-relaxed">
                            Three years on, Femcare is trusted by 2 million women across 80 countries — and
                            we&apos;re just getting started.
                        </p>
                    </div>
                </div>
            </section>

            {/* PRINCIPLES BENTO */}
            <section className="bg-femcare-petal/40 py-24 relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
                    <div className="text-center mb-14 reveal">
                        <p className="text-xs uppercase tracking-[0.3em] text-femcare-accent mb-4">
                            What we believe
                        </p>
                        <h2 className="editorial-headline text-4xl md:text-5xl text-femcare-purple max-w-3xl mx-auto">
                            Four principles that guide<br />
                            <span className="italic font-light">every decision.</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {PRINCIPLES.map((p, i) => (
                            <div
                                key={p.title}
                                className={`card-editorial p-8 reveal reveal-delay-${(i % 4) + 1}`}
                                data-testid={`principle-${p.title.replace(/ /g,'-').toLowerCase()}`}
                            >
                                <div className="w-12 h-12 rounded-full bg-femcare-pink/15 flex items-center justify-center mb-5">
                                    <p.icon className="w-5 h-5 text-femcare-pink" />
                                </div>
                                <h3 className="font-serif text-2xl text-femcare-purple mb-3">{p.title}</h3>
                                <p className="text-sm text-femcare-purple/70 leading-relaxed">{p.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* STATS — removed until verified */}

            {/* CTA */}
            <section className="max-w-5xl mx-auto px-6 lg:px-12 pt-20 pb-24 text-center reveal">
                <h2 className="editorial-headline text-3xl md:text-5xl text-femcare-purple mb-6">
                    Join the movement.
                </h2>
                <Link to="/app/signup" className="btn-primary magnet" data-testid="about-cta">
                    Get Femcare
                    <ArrowRight className="w-4 h-4" />
                </Link>
            </section>
        </div>
    );
}
